import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Check, FileText, BadgeCheck } from "lucide-react";
import { Reveal, SectionTag } from "./shared";
import { useLang } from "./i18n";

const COPY = {
  en: {
    tag: "The Report", headA: "One report. Your whole", headB: "trajectory.",
    bars: [["Logical reasoning", 92, "#1F5BFF"], ["Verbal ability", 84, "#17B6E0"], ["Spatial awareness", 77, "#7C3AED"], ["Numerical fluency", 88, "#3AA64B"]],
    bullets: ["Aptitude, personality, interest and EQ — scored and explained in plain language", "Ranked career clusters with transparent fit scores, not black-box answers", "Every report reviewed by a certified counselor before it reaches you"],
    quote: "It read less like a test result and more like someone finally explaining me to me.",
    reportTitle: "Bright IQ Report", sample: "Sample · Class 11 · Science stream", matches: "Top career matches",
    careers: ["Data Science", "Product Design", "Behavioural Economics"], verified: "AI-generated · Counselor reviewed", personality: "Personality", analyst: "The Analyst", fit: "Interest fit",
  },
  hi: {
    tag: "रिपोर्ट", headA: "एक रिपोर्ट। आपकी पूरी", headB: "दिशा।",
    bars: [["तार्किक तर्क", 92, "#1F5BFF"], ["भाषाई क्षमता", 84, "#17B6E0"], ["स्थानिक समझ", 77, "#7C3AED"], ["संख्यात्मक निपुणता", 88, "#3AA64B"]],
    bullets: ["योग्यता, व्यक्तित्व, रुचि और EQ — साफ़ भाषा में स्कोर और व्याख्या", "पारदर्शी फिट स्कोर के साथ क्रमबद्ध करियर क्लस्टर, कोई ब्लैक-बॉक्स नहीं", "हर रिपोर्ट आप तक पहुँचने से पहले प्रमाणित काउंसलर की समीक्षा से गुज़रती है"],
    quote: "यह परीक्षा-परिणाम कम और ऐसा लगा जैसे कोई मुझे मुझसे पहली बार समझा रहा हो।",
    reportTitle: "Bright IQ रिपोर्ट", sample: "नमूना · कक्षा 11 · विज्ञान स्ट्रीम", matches: "शीर्ष करियर मैच",
    careers: ["डेटा साइंस", "प्रोडक्ट डिज़ाइन", "व्यवहारिक अर्थशास्त्र"], verified: "AI-जनरेटेड · काउंसलर समीक्षित", personality: "व्यक्तित्व", analyst: "विश्लेषक", fit: "रुचि फिट",
  },
};

export default function Spotlight() {
  const { lang } = useLang();
  const copy = COPY[lang];
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const cardY = useTransform(scrollYProgress, [0, 1], [50, -50]);
  const chipY = useTransform(scrollYProgress, [0, 1], [90, -70]);

  return (
    <section id="report" ref={ref} className="relative bg-ink text-paper overflow-hidden">
      <div className="absolute inset-0 spotlight-glow pointer-events-none" aria-hidden />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-28 sm:py-36 grid lg:grid-cols-2 gap-16 items-center">
        <div>
          <SectionTag color="#17B6E0" dark>{copy.tag}</SectionTag>
          <Reveal>
            <h2 className="font-display font-bold text-3xl sm:text-5xl tracking-tight leading-[1.05] text-balance">
              {copy.headA} <span className="font-serifa italic font-normal text-iqcyan">{copy.headB}</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <ul className="mt-9 space-y-5">
              {copy.bullets.map((b) => (
                <li key={b} className="flex gap-4 items-start">
                  <span className="mt-0.5 h-6 w-6 shrink-0 rounded-full bg-iqcyan/15 flex items-center justify-center"><Check className="h-3.5 w-3.5 text-iqcyan" /></span>
                  <span className="text-paper/75 leading-relaxed">{b}</span>
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="mt-10 font-serifa italic text-xl text-paper/60 border-l-2 border-iqpink pl-5 max-w-md">"{copy.quote}"</p>
          </Reveal>
        </div>

        <div className="relative" style={{ perspective: 1000 }}>
          <motion.div style={{ y: cardY }} className="relative will-change-transform">
            <motion.div whileHover={{ rotate: 0, scale: 1.015 }} initial={{ rotate: 2.5 }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }} className="rounded-[1.8rem] bg-cream text-ink p-7 sm:p-9 shadow-[0_50px_100px_-20px_rgba(0,0,0,0.6)] overflow-hidden" data-testid="report-mock">
              <div className="flex items-center justify-between pb-5 border-b border-ink/10">
                <div className="flex items-center gap-3">
                  <img src="/brightiq-logo.png" alt="" className="h-9 w-9 rounded-lg object-contain" />
                  <div><div className="font-display font-bold text-sm leading-tight">{copy.reportTitle}</div><div className="text-[11px] text-ink/50">{copy.sample}</div></div>
                </div>
                <FileText className="h-5 w-5 text-ink/30" />
              </div>
              <div className="py-6 space-y-4">
                {copy.bars.map(([label, val, color], i) => (
                  <div key={label}>
                    <div className="flex justify-between text-xs font-semibold mb-1.5"><span>{label}</span><span style={{ color }}>{val}</span></div>
                    <div className="h-2 rounded-full bg-ink/8 overflow-hidden"><motion.div className="h-full rounded-full" style={{ background: color }} initial={{ width: 0 }} whileInView={{ width: `${val}%` }} viewport={{ once: true }} transition={{ duration: 1.2, delay: 0.2 + i * 0.12, ease: [0.16, 1, 0.3, 1] }} /></div>
                  </div>
                ))}
              </div>
              <div className="pt-5 border-t border-ink/10">
                <div className="text-[11px] font-semibold tracking-[0.2em] uppercase text-ink/45 mb-3">{copy.matches}</div>
                <div className="flex flex-wrap gap-2">{copy.careers.map((m) => <span key={m} className="text-xs font-semibold bg-ink text-paper px-3.5 py-1.5 rounded-full">{m}</span>)}</div>
                <div className="mt-5 flex items-center gap-2 text-xs text-ink/55"><BadgeCheck className="h-4 w-4 text-iqgreen" />{copy.verified}</div>
              </div>
            </motion.div>
          </motion.div>

          <motion.div style={{ y: chipY }} className="absolute -top-8 -right-2 sm:-right-6 rounded-2xl bg-white/10 backdrop-blur-md border border-white/15 px-5 py-3.5 shadow-xl">
            <div className="text-[10px] tracking-[0.2em] uppercase text-paper/55">{copy.personality}</div><div className="font-display font-bold text-lg">{copy.analyst}</div>
          </motion.div>
          <motion.div style={{ y: chipY }} className="absolute -bottom-8 -left-2 sm:-left-6 rounded-2xl bg-white/10 backdrop-blur-md border border-white/15 px-5 py-3.5 shadow-xl">
            <div className="text-[10px] tracking-[0.2em] uppercase text-paper/55">{copy.fit}</div><div className="font-display font-bold text-lg text-iqamber">94%</div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
